// ============================================
// COMPONENTE: Gráfico de Humor com Seletor de Tipo
// ============================================

import { useState } from 'react';
import {
  BarChart, Bar,
  PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const CORES = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC0CB', '#FFD700', '#00CED1', '#9370DB'];

type TipoGrafico = 'pizza' | 'barra' | 'radar';

interface Props {
  dados: Record<string, { 
    nome: string; 
    frequencia: number; 
    porcentagem: number;
  }>;
}

export default function GraficoHumor({ dados }: Props) {
  const [tipoGrafico, setTipoGrafico] = useState<TipoGrafico>('pizza');

  const dadosArray = Object.entries(dados || {})
    .map(([id, d]) => ({
      id,
      nome: d.nome,
      frequencia: d.frequencia,
      porcentagem: Math.round(d.porcentagem * 10) / 10
    }))
    .sort((a, b) => b.frequencia - a.frequencia);

  if (dadosArray.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-smile me-2"></i>
            Humor Predominante
          </h3>
        </div>
        <div className="card-body">
          <p className="text-muted text-center py-4">
            <i className="fas fa-smile fa-3x mb-3 d-block text-secondary"></i>
            Nenhum registro de humor no período
          </p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border rounded p-2 shadow-sm">
          <p className="mb-1"><strong>{data.nome}</strong></p>
          <p className="mb-0 text-primary">
            Frequência: {data.frequencia}x ({data.porcentagem}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderGrafico = () => {
    switch (tipoGrafico) {
      case 'barra':
        return (
          <BarChart 
            data={dadosArray} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            layout="horizontal"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="nome" 
              angle={-30}
              textAnchor="end"
              height={80}
              interval={0}
              tick={{ fontSize: 11 }}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="frequencia" name="Frequência" radius={[4, 4, 0, 0]}>
              {dadosArray.map((_, index) => (
                <Cell key={`cell-${index}`} fill={CORES[index % CORES.length]} />
              ))}
            </Bar>
          </BarChart>
        );

      case 'radar':
        return (
          <RadarChart cx="50%" cy="50%" outerRadius="80%" data={dadosArray}>
            <PolarGrid />
            <PolarAngleAxis dataKey="nome" tick={{ fontSize: 10 }} />
            <PolarRadiusAxis />
            <Radar 
              name="Frequência" 
              dataKey="frequencia" 
              stroke="#0088FE" 
              fill="#0088FE" 
              fillOpacity={0.5} 
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </RadarChart>
        );

      default: // pizza
        return (
          <PieChart>
            <Pie
              data={dadosArray}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ nome, porcentagem }: { nome: string; porcentagem: number }) => 
                porcentagem > 5 ? `${nome} (${porcentagem}%)` : ''
              }
              outerRadius={120}
              dataKey="frequencia"
              nameKey="nome"
            >
              {dadosArray.map((_, index) => (
                <Cell key={`cell-${index}`} fill={CORES[index % CORES.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        );
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
        <h3 className="card-title mb-0">
          <i className="fas fa-smile me-2"></i>
          Humor Predominante
        </h3>
        <div className="btn-group btn-group-sm">
          <button
            className={`btn ${tipoGrafico === 'pizza' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTipoGrafico('pizza')}
            title="Pizza"
          >
            <i className="fas fa-chart-pie"></i>
          </button>
          <button
            className={`btn ${tipoGrafico === 'barra' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTipoGrafico('barra')}
            title="Barras"
          >
            <i className="fas fa-chart-bar"></i>
          </button>
          <button
            className={`btn ${tipoGrafico === 'radar' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setTipoGrafico('radar')}
            title="Radar"
          >
            <i className="fas fa-spider"></i>
          </button>
        </div>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={350}>
          {renderGrafico()}
        </ResponsiveContainer>
        <div className="text-center mt-2">
          <small className="text-muted">
            Distribuição do humor registrado nas sessões
          </small>
        </div>
      </div>
    </div>
  );
}
